import { cn } from "@/lib/cn";
import { Icons } from "@/components/ui/icons";
import { Button } from "@/components/ui/button";
import { TextInput } from "@/components/ui/text-input";
import { Badge } from "@/components/ui/badge";

interface NewsletterFramePanelProps {
  subject: string;
  previewText: string;
  sections: Array<{ id: string; title: string }>;
  recipientCount: number;
  onSubjectChange?: (subject: string) => void;
  onPreviewTextChange?: (text: string) => void;
  onSend?: () => void;
  onSchedule?: () => void;
  onClose?: () => void;
  className?: string;
  "data-testid"?: string;
}

export function NewsletterFramePanel({
  subject,
  previewText,
  sections,
  recipientCount,
  onSubjectChange,
  onPreviewTextChange,
  onSend,
  onSchedule,
  onClose,
  className,
  "data-testid": testId = "newsletter-frame-panel",
}: NewsletterFramePanelProps) {
  return (
    <aside
      className={cn(
        "flex w-64 flex-col rounded-2xl border border-glass-stroke bg-glass-fill shadow-glass-sm backdrop-blur-glass-sm",
        className,
      )}
      data-testid={testId}
    >
      {/* Header */}
      <div className="flex h-11 items-center gap-2 border-b border-glass-stroke-subtle px-3.5">
        <Icons.Mail className="h-3.5 w-3.5 shrink-0 text-accent" aria-hidden="true" />
        <span className="text-xs font-semibold text-primary">Newsletter</span>
        <div className="flex-1" />
        <button
          type="button"
          onClick={onClose}
          className="flex items-center justify-center rounded-md p-0.5 text-muted transition-colors hover:text-primary"
          data-testid="newsletter-panel-close"
          aria-label="Close panel"
        >
          <Icons.X className="h-3.5 w-3.5" />
        </button>
      </div>

      {/* Scrollable content */}
      <div className="scrollbar-none flex flex-1 flex-col overflow-y-auto">
        {/* Subject */}
        <div className="flex flex-col gap-1 border-b border-glass-stroke-subtle px-3.5 py-3">
          <span className="text-[11px] font-semibold uppercase tracking-wide text-secondary">
            Subject
          </span>
          <TextInput
            value={subject}
            onChange={(e) => onSubjectChange?.(e.target.value)}
            placeholder="Email subject line"
            data-testid="newsletter-panel-subject"
          />
        </div>

        {/* Preview text */}
        <div className="flex flex-col gap-1 border-b border-glass-stroke-subtle px-3.5 py-3">
          <span className="text-[11px] font-semibold uppercase tracking-wide text-secondary">
            Preview Text
          </span>
          <TextInput
            value={previewText}
            onChange={(e) => onPreviewTextChange?.(e.target.value)}
            placeholder="Preview line shown in inbox"
            data-testid="newsletter-panel-preview-text"
          />
        </div>

        {/* Sections */}
        <div className="flex flex-col gap-1.5 border-b border-glass-stroke-subtle px-3.5 py-3">
          <span className="text-[11px] font-semibold uppercase tracking-wide text-secondary">
            Sections
          </span>
          <div className="flex flex-col gap-1">
            {sections.map((section) => (
              <div
                key={section.id}
                className="flex items-center gap-2 rounded-md px-2 py-1 text-[10px] text-secondary"
                data-testid={`newsletter-panel-section-${section.id}`}
              >
                <Icons.FileText className="h-3 w-3 shrink-0 text-muted" aria-hidden="true" />
                <span className="truncate">{section.title}</span>
              </div>
            ))}
            {sections.length === 0 && (
              <p className="text-[10px] text-muted">No sections</p>
            )}
          </div>
        </div>

        {/* Recipients */}
        <div className="flex items-center justify-between border-b border-glass-stroke-subtle px-3.5 py-3">
          <span className="text-[11px] font-semibold uppercase tracking-wide text-secondary">
            Recipients
          </span>
          <Badge variant="outline" data-testid="newsletter-panel-recipients">
            {recipientCount} subscribers
          </Badge>
        </div>

        {/* Action buttons */}
        <div className="flex flex-col gap-2 px-3.5 py-3">
          <Button
            variant="default"
            size="sm"
            onClick={onSend}
            data-testid="newsletter-panel-send"
          >
            <Icons.Send className="h-3 w-3" />
            Send Newsletter
          </Button>
          <Button
            variant="ghost"
            size="sm"
            onClick={onSchedule}
            data-testid="newsletter-panel-schedule"
          >
            <Icons.Clock className="h-3 w-3" />
            Schedule
          </Button>
        </div>
      </div>
    </aside>
  );
}

export type { NewsletterFramePanelProps };
